import {
  Blocks,
  ChartColumn,
  ChevronLeft,
  ChevronRight,
  CodeXml,
  Cpu,
  Eye,
  EyeOff,
  FileStack,
  Loader2,
  Lock,
  Mail,
  ShieldCheck,
  Wrench,
  Zap,
} from 'lucide-react';
import {
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type SubmitEvent,
  type UIEvent as ReactUIEvent,
} from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { BrandMark, BrandName } from '../../components/Brand';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input, Label } from '../../components/ui/input';
import { cn } from '../../lib/utils';
import { ThemeToggle } from '../theme/ThemeToggle';
import { useAuth } from './AuthContext';

// Every item here is a shipped capability (see docs/target/BACKEND-BACKLOG.md)
// — this is a product surface, so no roadmap promises.
const FEATURES = [
  {
    icon: ShieldCheck,
    title: 'Guardrails on every answer',
    text: 'Retrieved knowledge is screened for prompt injection, and replies for PII, outside the model.',
  },
  {
    icon: Wrench,
    title: 'Tool calling',
    text: 'Assistants call your own APIs to check availability or look up an order, with a full audit trail.',
  },
  {
    icon: Blocks,
    title: 'Domain blueprints',
    text: 'Start from a ready-made industry template, or author your own. A new vertical needs no code.',
  },
  {
    icon: FileStack,
    title: 'Any knowledge source',
    text: 'Text, web pages and file uploads, chunked, embedded and made searchable automatically.',
  },
  {
    icon: CodeXml,
    title: 'One-tag website widget',
    text: 'Origin-locked and fully themeable. No tenant secret ever ships to the page.',
  },
  {
    icon: Zap,
    title: 'Streaming, grounded replies',
    text: 'Answers stream word by word from your content, with the passages shown for review.',
  },
  {
    icon: Cpu,
    title: 'Bring your own model',
    text: 'Gemini, OpenAI and more behind one provider-agnostic gateway, with per-assistant keys.',
  },
  {
    icon: ChartColumn,
    title: 'Usage & cost analytics',
    text: 'Tokens, spend and visitor feedback per assistant and model, plus a full audit log.',
  },
];

const STATS = [
  { value: '100%', label: 'Tenant isolated' },
  { value: '1', label: 'Script tag to embed' },
  { value: '24/7', label: 'Guardrails active' },
];

const PREFERS_REDUCED_MOTION =
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function LoginPage() {
  const { session, login } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [cardHovering, setCardHovering] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);

  const pageRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  if (session) {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      await login(email, password);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function handlePageMove(event: ReactMouseEvent<HTMLDivElement>) {
    if (PREFERS_REDUCED_MOTION) return;
    const el = pageRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty('--spot-x', `${((event.clientX - rect.left) / rect.width) * 100}%`);
    el.style.setProperty('--spot-y', `${((event.clientY - rect.top) / rect.height) * 100}%`);
  }

  function handleCardMove(event: ReactMouseEvent<HTMLDivElement>) {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (event.clientX - rect.left) / rect.width;
    const py = (event.clientY - rect.top) / rect.height;
    el.style.setProperty('--card-x', `${px * 100}%`);
    el.style.setProperty('--card-y', `${py * 100}%`);
    if (!PREFERS_REDUCED_MOTION) {
      el.style.setProperty('--tilt-x', `${(0.5 - py) * 8}deg`);
      el.style.setProperty('--tilt-y', `${(px - 0.5) * 8}deg`);
    }
  }

  function handleCardLeave() {
    const el = cardRef.current;
    if (el) {
      el.style.setProperty('--tilt-x', '0deg');
      el.style.setProperty('--tilt-y', '0deg');
    }
    setCardHovering(false);
  }

  // Mobile only: the form and the product details live in a horizontally
  // snapping carousel (form first, so signing in never requires a scroll)
  // rather than a vertical stack the user has to scroll past. Desktop
  // ignores this entirely — `lg:` classes turn the same markup back into
  // the plain side-by-side layout.
  function handleCarouselScroll(event: ReactUIEvent<HTMLDivElement>) {
    const el = event.currentTarget;
    if (el.clientWidth === 0) return;
    setActiveSlide(Math.round(el.scrollLeft / el.clientWidth));
  }

  function scrollToSlide(index: number) {
    const el = carouselRef.current;
    if (!el) return;
    el.scrollTo({ left: index * el.clientWidth, behavior: 'smooth' });
    setActiveSlide(index);
  }

  return (
    <div
      ref={pageRef}
      onMouseMove={handlePageMove}
      className="surface-grid relative min-h-screen overflow-hidden bg-background"
    >
      {/* Ambient layer — one shared canvas across the whole screen, fully
          theme-driven, so there is no seam between "brand" and "form" and no
          colour that only makes sense in one theme. A slow radar-style beam
          plus two drifting glows stand in for motion, no shooting stars. */}
      <div aria-hidden className="page-spotlight pointer-events-none absolute inset-0" />
      <div aria-hidden className="grid-beam pointer-events-none absolute inset-0" />
      <div
        aria-hidden
        className="aurora-blob pointer-events-none absolute top-[6%] left-[8%] size-64 rounded-full bg-primary/20 blur-[100px] sm:size-80 lg:left-[12%] lg:size-[26rem]"
      />
      <div
        aria-hidden
        className="aurora-blob pointer-events-none absolute right-[6%] bottom-[6%] size-56 rounded-full bg-primary/15 blur-[100px] sm:size-72 lg:right-[10%] lg:size-[22rem]"
        style={{ animationDelay: '-9s' }}
      />

      <div className="absolute top-4 right-4 z-20">
        <ThemeToggle />
      </div>

      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-[88rem] flex-col justify-center px-0 py-16 sm:px-6 lg:px-10">
        <div
          ref={carouselRef}
          onScroll={handleCarouselScroll}
          className={cn(
            'flex w-full overflow-x-auto scroll-smooth [scrollbar-width:none]',
            'snap-x snap-mandatory [&::-webkit-scrollbar]:hidden',
            'lg:snap-none lg:overflow-visible lg:flex-row-reverse lg:items-center lg:justify-between lg:gap-8 xl:gap-14',
          )}
        >
          {/* Slide 1 (default, no scroll needed) — the login form. A fixed
              width on desktop: as an auto-width flex item it would shrink to
              its narrowest content and ignore its own max-width. */}
          <div className="flex w-full shrink-0 snap-center justify-center px-4 sm:px-0 lg:w-[26rem] lg:justify-start lg:px-0 lg:[scroll-snap-align:none] xl:w-[28rem]">
            <div className="rise-in relative w-full max-w-md lg:max-w-none">
              <div className="mb-7 text-center lg:text-left">
                <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Welcome back</h1>
                <p className="mt-2 text-sm text-muted-foreground sm:text-base">
                  Sign in to manage your assistants.
                </p>
              </div>

              <div
                ref={cardRef}
                onMouseMove={handleCardMove}
                onMouseEnter={() => setCardHovering(true)}
                onMouseLeave={handleCardLeave}
                className="transition-transform duration-150 ease-out [transform-style:preserve-3d]"
                style={{
                  transform:
                    'perspective(1000px) rotateX(var(--tilt-x, 0deg)) rotateY(var(--tilt-y, 0deg))',
                }}
              >
                <Card className="relative overflow-hidden border-border-strong/60 bg-surface/90 shadow-2xl shadow-black/10 backdrop-blur-sm">
                  <div
                    aria-hidden
                    className="card-spotlight pointer-events-none absolute inset-0 transition-opacity duration-300"
                    style={{ opacity: cardHovering ? 1 : 0 }}
                  />
                  <div aria-hidden className="scan-line absolute inset-x-0 top-0 h-px" />
                  <CardContent className="relative p-6 sm:p-8">
                    <form className="grid gap-5" onSubmit={handleSubmit} noValidate>
                      <div className="grid gap-2">
                        <Label htmlFor="email">Email</Label>
                        <div className="relative">
                          <Mail
                            aria-hidden
                            className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-muted-foreground"
                          />
                          <Input
                            id="email"
                            type="email"
                            autoComplete="email"
                            required
                            autoFocus
                            placeholder="you@company.com"
                            className="h-11 pl-10 text-[0.9375rem]"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="grid gap-2">
                        <Label htmlFor="password">Password</Label>
                        <div className="relative">
                          <Lock
                            aria-hidden
                            className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-muted-foreground"
                          />
                          <Input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="current-password"
                            required
                            placeholder="••••••••"
                            className="h-11 pr-10 pl-10 text-[0.9375rem]"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            aria-pressed={showPassword}
                            className="absolute top-1/2 right-3 -translate-y-1/2 rounded-sm p-0.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
                          >
                            {showPassword ? (
                              <EyeOff aria-hidden className="size-4" />
                            ) : (
                              <Eye aria-hidden className="size-4" />
                            )}
                          </button>
                        </div>
                      </div>

                      {error && (
                        <p
                          role="alert"
                          className="rounded-[var(--radius-control)] bg-danger-muted px-3 py-2 text-sm text-danger"
                        >
                          {error}
                        </p>
                      )}

                      <Button
                        type="submit"
                        size="lg"
                        disabled={busy}
                        className="btn-aurora mt-1 h-11 w-full text-[0.9375rem]"
                      >
                        {busy && <Loader2 className="animate-spin" />}
                        {busy ? 'Signing in' : 'Sign in'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>

              <div className="mt-6 flex justify-center">
                <Badge tone="success" dot pulse>
                  Encrypted session
                </Badge>
              </div>
            </div>
          </div>

          {/* Desktop-only divider between the product details and the form
              (below lg the two are swipe slides, so it's hidden). Stretches to
              the row's height; the line fades out at both ends, warms to the
              accent at the centre node, and a soft light travels down it — the
              same scan motif as the card's top edge. Styles: .login-divider*
              in styles.css. */}
          <div aria-hidden className="login-divider hidden shrink-0 self-stretch lg:block">
            <span className="login-divider-line" />
            <span className="login-divider-beam" />
            <span className="login-divider-node" />
          </div>

          {/* Slide 2 (swipe to reach) — product details, side-by-side with
              the form on desktop instead of a second slide. */}
          <div className="flex w-full shrink-0 snap-center justify-center px-4 sm:px-0 lg:w-auto lg:min-w-0 lg:flex-1 lg:shrink lg:justify-start lg:px-0 lg:[scroll-snap-align:none]">
            <div className="w-full max-w-xl text-center lg:max-w-3xl lg:text-left">
              <div className="flex items-center justify-center gap-3 lg:justify-start">
                <BrandMark className="size-12 shadow-[0_0_30px_-6px_var(--brand-via)]" />
                <BrandName className="text-3xl" />
              </div>

              <h2 className="mt-6 text-2xl leading-tight font-semibold tracking-tight text-foreground sm:text-3xl xl:text-4xl">
                Multi-tenant RAG chatbots,
                <br />
                <span className="text-gradient-accent">one platform.</span>
              </h2>
              <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base lg:mx-0">
                Every business gets an isolated assistant with its own knowledge, guardrails, tools
                and website widget, without a line of custom code.
              </p>

              {/* Below lg the two halves are swipe slides sharing one height,
                  so the grid stays compact there (icon + title) — descriptions
                  would make this slide far taller than the form beside it. */}
              <ul className="mt-8 grid grid-cols-2 gap-2 text-left sm:gap-3">
                {FEATURES.map(({ icon: Icon, title, text }, i) => (
                  <li
                    key={title}
                    className="rise-in group flex items-center gap-2.5 rounded-[var(--radius-card)] border border-border/70 bg-surface/60 p-2.5 backdrop-blur-sm sm:gap-3 lg:items-start lg:p-3.5 transition-colors hover:border-primary/40 hover:bg-surface/90"
                    style={{ animationDelay: `${150 + i * 70}ms` }}
                  >
                    <span className="grid size-8 shrink-0 place-items-center rounded-[var(--radius-control)] bg-primary-muted text-primary ring-1 ring-primary/20 transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                      <Icon aria-hidden className="size-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-foreground">{title}</span>
                      <span className="mt-0.5 hidden text-xs leading-relaxed text-muted-foreground lg:block">
                        {text}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>

              <div className="mx-auto mt-8 flex max-w-md justify-center gap-6 border-t border-border pt-6 sm:gap-10 lg:mx-0 lg:max-w-none lg:justify-start">
                {STATS.map((s) => (
                  <div key={s.label} className="border-l-2 border-primary/40 pl-3 text-left">
                    <div className="font-mono text-xl font-semibold text-foreground">{s.value}</div>
                    <div className="label-micro">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile-only paging: dots plus a hint so the second slide (the
            product details) isn't undiscoverable. */}
        <div className="mt-6 flex flex-col items-center gap-2 lg:hidden">
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => scrollToSlide(0)}
              aria-label="Show login form"
              aria-current={activeSlide === 0}
              className={cn(
                'h-1.5 rounded-full transition-all',
                activeSlide === 0 ? 'w-6 bg-primary' : 'w-1.5 bg-border-strong',
              )}
            />
            <button
              type="button"
              onClick={() => scrollToSlide(1)}
              aria-label="Show product details"
              aria-current={activeSlide === 1}
              className={cn(
                'h-1.5 rounded-full transition-all',
                activeSlide === 1 ? 'w-6 bg-primary' : 'w-1.5 bg-border-strong',
              )}
            />
          </div>
          <button
            type="button"
            onClick={() => scrollToSlide(activeSlide === 0 ? 1 : 0)}
            className="label-micro flex items-center gap-1 text-muted-foreground transition-colors hover:text-foreground"
          >
            {activeSlide === 0 ? (
              <>
                Swipe for product details <ChevronRight aria-hidden className="size-3" />
              </>
            ) : (
              <>
                <ChevronLeft aria-hidden className="size-3" /> Back to sign in
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
